import { useState, useRef, useEffect, useContext } from 'react';
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff, X, Monitor, MonitorOff } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ]
};

const UNANSWERED_TIMEOUT_MS = 60000;

const VideoCall = ({ isOpen, onClose, remoteUser, callType = 'video' }) => {
  const { user, token } = useContext(AuthContext);
  const [callStatus, setCallStatus] = useState('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(callType === 'audio');
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const callTimerRef = useRef(null);
  const callIdRef = useRef(null);
  const dialingAudioRef = useRef(null);
  const iceBufferRef = useRef([]);
  const processedIceRef = useRef(0);
  const unansweredTimerRef = useRef(null);
  const answerSetRef = useRef(false);

  useEffect(() => {
    if (isOpen && remoteUser) {
      initiateCall();
    }
    return () => {
      cleanup();
    };
  }, [isOpen, remoteUser]);

  useEffect(() => {
    if (callStatus === 'connected') {
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
    };
  }, [callStatus]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const playDialingSound = () => {
    try {
      if (!dialingAudioRef.current) {
        dialingAudioRef.current = new Audio('/audio/dialing.wav');
        dialingAudioRef.current.loop = true;
      }
      dialingAudioRef.current.currentTime = 0;
      dialingAudioRef.current.play().catch((e) => console.debug('Audio autoplay blocked:', e.message));
    } catch (e) { console.debug('Dialing sound error:', e.message); }
  };

  const stopDialingSound = () => {
    try {
      if (dialingAudioRef.current) {
        dialingAudioRef.current.pause();
        dialingAudioRef.current.currentTime = 0;
      }
    } catch (e) { console.debug('Stop dialing error:', e.message); }
  };

  const cleanup = () => {
    stopDialingSound();
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }
    if (unansweredTimerRef.current) {
      clearTimeout(unansweredTimerRef.current);
    }
    callIdRef.current = null;
    iceBufferRef.current = [];
    processedIceRef.current = 0;
    answerSetRef.current = false;
    setCallStatus('idle');
    setCallDuration(0);
  };

  const initiateCall = async () => {
    try {
      setCallStatus('calling');

      const stream = await navigator.mediaDevices.getUserMedia({
        video: callType === 'video',
        audio: true
      });

      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      const pc = new RTCPeerConnection(ICE_SERVERS);
      peerConnectionRef.current = pc;

      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });

      pc.ontrack = (event) => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
        }
        stopDialingSound();
        setCallStatus('connected');
      };

      // Buffer ICE candidates until call_id is available, then flush
      pc.onicecandidate = async (event) => {
        if (event.candidate) {
          const candidateStr = JSON.stringify(event.candidate);
          if (callIdRef.current) {
            try {
              await axios.post(`${API}/calls/${callIdRef.current}/ice-candidate`, {
                candidate: candidateStr
              }, { headers: { Authorization: `Bearer ${token}` } });
            } catch (err) { console.debug('Call error:', err.message); }
          } else {
            iceBufferRef.current.push(candidateStr);
          }
        }
      };

      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'failed') {
          endCall();
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const response = await axios.post(`${API}/calls/initiate`, {
        receiver_id: remoteUser.id,
        call_type: callType,
        offer: JSON.stringify(offer)
      }, { headers: { Authorization: `Bearer ${token}` } });

      callIdRef.current = response.data.call_id;

      // Flush buffered ICE candidates
      for (const c of iceBufferRef.current) {
        try {
          await axios.post(`${API}/calls/${callIdRef.current}/ice-candidate`, {
            candidate: c
          }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (err) { console.debug('Call error:', err.message); }
      }
      iceBufferRef.current = [];

      playDialingSound();

      // Unanswered timeout
      unansweredTimerRef.current = setTimeout(() => {
        if (!answerSetRef.current) {
          toast.info('Call not answered');
          endCall();
        }
      }, UNANSWERED_TIMEOUT_MS);

      // Start polling for answer, ICE candidates, and status
      pollingIntervalRef.current = setInterval(async () => {
        try {
          const statusResponse = await axios.get(`${API}/calls/${callIdRef.current}/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const { status, answer, ice_candidates } = statusResponse.data;

          // Set answer once
          if (status === 'answered' && answer && !answerSetRef.current) {
            answerSetRef.current = true;
            stopDialingSound();
            if (unansweredTimerRef.current) clearTimeout(unansweredTimerRef.current);
            const answerDesc = JSON.parse(answer);
            await pc.setRemoteDescription(new RTCSessionDescription(answerDesc));
          }

          // Process new ICE candidates (only after answer is set)
          if (ice_candidates && answerSetRef.current) {
            const newCandidates = ice_candidates.slice(processedIceRef.current);
            for (const candidateStr of newCandidates) {
              try {
                const candidate = JSON.parse(candidateStr);
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
              } catch (e) { console.debug('Call error:', e.message); }
            }
            processedIceRef.current = ice_candidates.length;
          }

          if (status === 'declined') {
            stopDialingSound();
            toast.info(`${remoteUser?.name} declined the call`);
            cleanup();
            onClose();
          } else if (status === 'ended') {
            stopDialingSound();
            cleanup();
            onClose();
          }
        } catch (err) { console.debug('Call error:', err.message); }
      }, 1500);

    } catch (error) {
      console.error('Call initiation error:', error);
      toast.error('Could not start call — check microphone/camera permissions');
      setCallStatus('ended');
    }
  };

  const endCall = async () => {
    try {
      if (callIdRef.current) {
        await axios.post(`${API}/calls/${callIdRef.current}/end`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (err) { console.debug('Call error:', err.message); }
    cleanup();
    onClose();
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  const screenSharingRef = useRef(false);

  const stopScreenShare = async () => {
    if (!peerConnectionRef.current) return;
    try {
      const camStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      const camTrack = camStream.getVideoTracks()[0];
      const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
      if (sender) {
        await sender.replaceTrack(camTrack);
      }
      const oldTrack = localStreamRef.current?.getVideoTracks()[0];
      if (oldTrack) oldTrack.stop();
      if (localStreamRef.current) {
        localStreamRef.current.removeTrack(oldTrack);
        localStreamRef.current.addTrack(camTrack);
      }
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }
      screenSharingRef.current = false;
      setIsScreenSharing(false);
      setIsVideoOff(false);
    } catch (err) {
      toast.error('Failed to switch back to camera');
    }
  };

  const toggleScreenShare = async () => {
    if (!peerConnectionRef.current) return;
    
    if (screenSharingRef.current) {
      await stopScreenShare();
    } else {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack = screenStream.getVideoTracks()[0];
        const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          await sender.replaceTrack(screenTrack);
        }
        screenTrack.onended = () => {
          stopScreenShare();
        };
        const oldTrack = localStreamRef.current?.getVideoTracks()[0];
        if (oldTrack) oldTrack.stop();
        if (localStreamRef.current) {
          localStreamRef.current.removeTrack(oldTrack);
          localStreamRef.current.addTrack(screenTrack);
        }
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStreamRef.current;
        }
        screenSharingRef.current = true;
        setIsScreenSharing(true);
        setIsVideoOff(false);
      } catch (err) {
        // User cancelled the screen share picker
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">
      <div className="relative w-full h-full max-w-4xl max-h-[90vh] bg-gray-900 rounded-2xl overflow-hidden">
        {/* Remote video (full screen) */}
        <div className="absolute inset-0">
          {callType === 'video' ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
              <audio ref={remoteVideoRef} autoPlay />
              <div className="text-center">
                <Avatar className="w-32 h-32 mx-auto mb-4 border-4 border-white">
                  <AvatarImage src={remoteUser?.profile_photo} />
                  <AvatarFallback className="bg-coral text-white text-4xl">
                    {remoteUser?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <h2 className="text-white text-2xl font-bold">{remoteUser?.name}</h2>
                <p className="text-gray-400 mt-2">
                  {callStatus === 'calling' ? 'Calling...' :
                   callStatus === 'connected' ? formatDuration(callDuration) :
                   'Connecting...'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Local video (small overlay) */}
        {callType === 'video' && (
          <div className="absolute bottom-24 right-4 w-32 h-44 rounded-xl overflow-hidden border-2 border-white shadow-lg">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className={`w-full h-full object-cover ${isVideoOff ? 'hidden' : ''}`}
            />
            {isVideoOff && (
              <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                <VideoOff className="w-8 h-8 text-gray-400" />
              </div>
            )}
          </div>
        )}

        {/* Calling overlay for video calls */}
        {callType === 'video' && callStatus === 'calling' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 z-10">
            <div className="text-center">
              <Avatar className="w-24 h-24 mx-auto mb-4 border-4 border-white animate-pulse">
                <AvatarImage src={remoteUser?.profile_photo} />
                <AvatarFallback className="bg-coral text-white text-3xl">
                  {remoteUser?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <p className="text-white text-xl">Calling {remoteUser?.name}...</p>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black to-transparent">
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={toggleMute}
              className={`p-4 rounded-full ${isMuted ? 'bg-red-500' : 'bg-gray-700 hover:bg-gray-600'} transition-colors`}
            >
              {isMuted ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-white" />}
            </button>

            <button
              onClick={endCall}
              className="p-5 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
            >
              <PhoneOff className="w-8 h-8 text-white" />
            </button>

            {callType === 'video' && (
              <button
                onClick={toggleVideo}
                className={`p-4 rounded-full ${isVideoOff ? 'bg-red-500' : 'bg-gray-700 hover:bg-gray-600'} transition-colors`}
              >
                {isVideoOff ? <VideoOff className="w-6 h-6 text-white" /> : <Video className="w-6 h-6 text-white" />}
              </button>
            )}

            {callType === 'video' && callStatus === 'connected' && (
              <button
                data-testid="screen-share-btn"
                onClick={toggleScreenShare}
                className={`p-4 rounded-full ${isScreenSharing ? 'bg-blue-500' : 'bg-gray-700 hover:bg-gray-600'} transition-colors`}
                title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
              >
                {isScreenSharing ? <MonitorOff className="w-6 h-6 text-white" /> : <Monitor className="w-6 h-6 text-white" />}
              </button>
            )}
          </div>

          {callType === 'video' && callStatus === 'connected' && (
            <p className="text-center text-white text-sm mt-4">
              {formatDuration(callDuration)}
            </p>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={endCall}
          className="absolute top-4 right-4 p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 transition-colors"
        >
          <X className="w-6 h-6 text-white" />
        </button>
      </div>
    </div>
  );
};

export default VideoCall;
